// GpsCompassProvider — M1 implementation of the PlacementProvider seam (plan §4).
//
// One-shot alignment: wait for (GPS accuracy gate) ∧ (heading stable) ∧ (camera
// running), then orient+position a worldGroup so ENU content lands correctly in
// engine space; after that, SLAM owns the frame. Never continuously slaved to GPS.
//
// ALIGNMENT MATH (derived + hand-verified; field-check signs at M1 test):
// Let α(v) = atan2(v.x, v.z) for horizontal vectors in engine world space —
// α increases counterclockwise viewed from above (+Y).
//   · Camera forward f (camera.getWorldDirection, y-flattened): αf = atan2(f.x, f.z)
//   · Compass: forward points at heading h (deg CW from north) ⇒ north's angle
//     αn = αf + h (CW is negative α, so undoing it adds h)
//   · worldGroup local convention: north = local −Z. Local −Z under rotation.y=θ
//     maps to angle θ + π. Require θ + π = αn ⇒  θ = αf + h − π
// Position: worldGroup origin = camera XZ at alignment (y = 0, engine ground).
import * as THREE from 'three'
import type {GeoPoint} from './geo'
import {enuOffset, enuToLocal, geoDistanceMeters} from './geo'
import type {GpsTracker} from './gps'
import {startGps} from './gps'
import type {HeadingTracker} from './heading'
import {startHeading} from './heading'

export type AlignmentState =
  | 'starting'
  | 'waiting-gps'
  | 'waiting-heading'
  | 'aligned'
  | 'gps-denied'

export interface DebugSnapshot {
  state: AlignmentState
  gps: {lat: number; lng: number; accuracy: number; ageMs: number} | null
  gpsStreak: number
  gpsError: string | null
  headingDeg: number | null
  headingSource: string | null
  headingSpreadDeg: number | null
  cameraYawDeg: number | null
  appliedThetaDeg: number | null
  originFix: GeoPoint | null
  driftM: number | null
  alignedAtMs: number | null
}

export interface GpsCompassProvider {
  readonly state: AlignmentState
  readonly worldGroup: THREE.Group
  /** Call every frame with the AR camera. */
  update: (camera: THREE.PerspectiveCamera) => void
  /** Scene-space position for a geo point relative to the ALIGNMENT origin (null until aligned). */
  poseFor: (geo: GeoPoint) => THREE.Vector3 | null
  /**
   * Scene-space position for a geo point relative to WHERE THE USER IS NOW
   * (current camera + fresh GPS fix). Re-origins the ENU baseline to the user at
   * this moment, so placement error doesn't accumulate over the distance walked
   * since alignment — use when spawning content (Unity-parity drift reduction).
   * Null when not aligned or no fresh good fix (fall back to poseFor).
   */
  placeRelativeToUser: (geo: GeoPoint, camera: THREE.PerspectiveCamera) => THREE.Vector3 | null
  /** Re-run the alignment gate from current sensors. */
  recenter: () => void
  /**
   * Reset the drift baseline to the user's current position (call after content
   * is re-origined near the user, so accumulated whole-walk error doesn't keep
   * flagging drift that the freshly placed content doesn't actually have).
   */
  rebaseDrift: (camera: THREE.PerspectiveCamera) => void
  /** True when sustained GPS-vs-scene disagreement suggests recentering. */
  readonly driftSuspected: boolean
  readonly debug: DebugSnapshot
  onStateChange?: (state: AlignmentState) => void
  stop: () => void
}

const HEADING_SPREAD_GATE_DEG = 6
const DRIFT_SUGGEST_M = 25
const DRIFT_SUSTAIN_MS = 10000
const FRESH_FIX_MAX_AGE_MS = 10000
const UP = new THREE.Vector3(0, 1, 0)

export function createGpsCompassProvider(): GpsCompassProvider {
  const worldGroup = new THREE.Group()
  worldGroup.name = 'geo-world'

  let state: AlignmentState = 'starting'
  let gps: GpsTracker | null = null
  let heading: HeadingTracker | null = null
  let originFix: GeoPoint | null = null
  let appliedTheta: number | null = null
  let cameraYawAtAlign: number | null = null
  let alignedAt: number | null = null
  let wantAlign = true
  let driftSince: number | null = null
  let driftM: number | null = null
  let lastCamera: THREE.PerspectiveCamera | null = null
  // Drift is measured as displacement-since-base (GPS vs SLAM), so it can be
  // re-based when content is re-origined near the user.
  let driftBase: {fix: GeoPoint; camX: number; camZ: number} | null = null

  const self: GpsCompassProvider = {
    get state() {
      return state
    },
    worldGroup,
    update,
    poseFor,
    placeRelativeToUser: (geo: GeoPoint, camera: THREE.PerspectiveCamera) => {
      // STABLE fix only — a lone multipath fix at load-completion would misplace
      // content that never gets corrected afterward (review finding). Callers
      // fall back to the alignment-origin placement when this returns null.
      const fix = gps?.stableFix
      if (
        state !== 'aligned' ||
        appliedTheta === null ||
        !fix ||
        Date.now() - fix.timestamp > FRESH_FIX_MAX_AGE_MS
      ) {
        return null
      }
      // ENU offset from the user's CURRENT fix, rotated by the alignment yaw,
      // anchored at the camera's CURRENT scene position.
      const v = new THREE.Vector3(...enuToLocal(enuOffset(fix, geo)))
      v.applyAxisAngle(UP, appliedTheta)
      v.x += camera.position.x
      v.z += camera.position.z
      v.y = 0
      return v
    },
    rebaseDrift: (camera: THREE.PerspectiveCamera) => {
      const fix = gps?.stableFix
      if (!fix) return
      driftBase = {fix: {lat: fix.lat, lng: fix.lng}, camX: camera.position.x, camZ: camera.position.z}
      driftSince = null
      driftM = null
    },
    recenter: () => {
      wantAlign = true
      driftSince = null
      setState('waiting-gps')
    },
    get driftSuspected() {
      return driftSince !== null && Date.now() - driftSince > DRIFT_SUSTAIN_MS
    },
    get debug() {
      const fix = gps?.last ?? null
      return {
        state,
        gps: fix
          ? {lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy, ageMs: Date.now() - fix.timestamp}
          : null,
        gpsStreak: gps?.goodStreak ?? 0,
        gpsError: gps?.error ?? null,
        headingDeg: heading?.meanDeg ?? heading?.current?.headingDeg ?? null,
        headingSource: heading?.current?.source ?? null,
        headingSpreadDeg: heading?.spreadDeg ?? null,
        cameraYawDeg: cameraYawAtAlign !== null ? toDeg(cameraYawAtAlign) : currentCameraYawDeg(),
        appliedThetaDeg: appliedTheta !== null ? toDeg(appliedTheta) : null,
        originFix,
        driftM,
        alignedAtMs: alignedAt,
      }
    },
    stop: () => {
      gps?.stop()
      heading?.stop()
    },
  }

  function setState(next: AlignmentState) {
    if (state === next) return
    state = next
    self.onStateChange?.(next)
  }

  // Sensors start immediately: heading listeners are permission-gated by the
  // motion prompt (already granted before AR), GPS triggers the location prompt.
  heading = startHeading()
  gps = startGps()
  setState('waiting-gps')

  const forward = new THREE.Vector3()

  function currentCameraYawDeg(): number | null {
    if (!lastCamera) return null
    lastCamera.getWorldDirection(forward)
    return toDeg(Math.atan2(forward.x, forward.z))
  }

  function update(camera: THREE.PerspectiveCamera) {
    lastCamera = camera
    if (gps?.error && state !== 'aligned') setState('gps-denied')
    if (wantAlign) tryAlign(camera)
    if (state === 'aligned') monitorDrift(camera)
  }

  function tryAlign(camera: THREE.PerspectiveCamera) {
    // Unity-parity gate: require a STABLE fix (consecutive agreeing good fixes),
    // not just one fix that claims low accuracy — first fixes are often stale.
    const fix = gps?.stableFix
    if (!fix || Date.now() - fix.timestamp > 15000) {
      if (state !== 'gps-denied') setState('waiting-gps')
      return
    }
    const h = heading?.meanDeg
    const spread = heading?.spreadDeg
    if (h === null || h === undefined || spread === null || spread === undefined) {
      setState('waiting-heading')
      return
    }
    if (spread > HEADING_SPREAD_GATE_DEG) {
      setState('waiting-heading')
      return
    }

    // Gates passed — align now.
    camera.getWorldDirection(forward)
    forward.y = 0
    if (forward.lengthSq() < 1e-6) return // camera pointing straight down/up
    forward.normalize()
    const alphaF = Math.atan2(forward.x, forward.z)
    const theta = alphaF + (h * Math.PI) / 180 - Math.PI

    worldGroup.position.set(camera.position.x, 0, camera.position.z)
    worldGroup.rotation.set(0, theta, 0)

    originFix = {lat: fix.lat, lng: fix.lng}
    appliedTheta = theta
    cameraYawAtAlign = alphaF
    alignedAt = Date.now()
    wantAlign = false
    driftSince = null
    driftM = null
    driftBase = {fix: originFix, camX: camera.position.x, camZ: camera.position.z}
    setState('aligned')
  }

  const expected = new THREE.Vector3()

  function monitorDrift(camera: THREE.PerspectiveCamera) {
    const fix = gps?.lastGood
    if (!fix || !driftBase || appliedTheta === null) return
    // A stale fix + continued SLAM movement reads as fake drift (covered arcade
    // scenario) — only judge drift against a FRESH fix.
    if (Date.now() - fix.timestamp > FRESH_FIX_MAX_AGE_MS) {
      driftM = null
      driftSince = null
      return
    }
    // GPS-implied displacement since the base vs SLAM displacement since the base.
    expected.set(...enuToLocal(enuOffset(driftBase.fix, fix)))
    expected.applyAxisAngle(UP, appliedTheta)
    const dx = expected.x - (camera.position.x - driftBase.camX)
    const dz = expected.z - (camera.position.z - driftBase.camZ)
    const d = Math.hypot(dx, dz)
    driftM = d
    if (d > DRIFT_SUGGEST_M) {
      if (driftSince === null) driftSince = Date.now()
    } else {
      driftSince = null
    }
  }

  function poseFor(geo: GeoPoint): THREE.Vector3 | null {
    if (state !== 'aligned' || !originFix) return null
    const v = new THREE.Vector3(...enuToLocal(enuOffset(originFix, geo)))
    return worldGroup.localToWorld(v)
  }

  return self
}

function toDeg(rad: number): number {
  return ((rad * 180) / Math.PI + 360) % 360
}

export function distanceToGeoM(origin: GeoPoint, target: GeoPoint): number {
  return geoDistanceMeters(origin, target)
}
